import { useEffect, useState } from "react";
import { FiArrowRight, FiCheckCircle, FiMapPin, FiShield } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import ReviewShipmentCard from "../../components/Checkout/ReviewShipmentCard";
import CouponForm from "../../components/Checkout/CouponForm";
import OrderSummary from "../../components/Checkout/OrderSummary";
import PaymentMethodSelector from "../../components/Checkout/PaymentMethodSelector";
import {
  getCheckoutReview,
  applyCouponCheckout,
  startPayment,
} from "../../services/checkoutApi";

export default function CheckoutReviewPage() {
  const [checkout, setCheckout] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [orderNote, setOrderNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [isPaying, setIsPaying] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCheckout();
  }, []);

  const fetchCheckout = async () => {
    try {
      const res = await getCheckoutReview();
      setCheckout(res.data);
    } catch (error) {
      if (error.response?.data?.error === "checkout_expired") {
        alert(
          "سشن خرید شما منقضی شده یا سبد خرید تغییر کرده است. لطفاً دوباره روش‌های ارسال را انتخاب کنید."
        );
        navigate("/checkout/checkout/");
        return;
      }

      console.error("خطا در بارگذاری اطلاعات:", error);
      alert("خطایی در سیستم رخ داده است.");
    }
  };

  const applyCoupon = async (code) => {
    try {
      const res = await applyCouponCheckout(code);

      setCouponCode(code);
      setCheckout((prev) => ({
        ...prev,
        coupon_amount: res.data.discount_value,
        payable_amount: res.data.payable_amount,
      }));

      toast.success("کد تخفیف با موفقیت اعمال شد.");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "خطایی رخ داد";
      toast.error(errorMessage);
    }
  };

  const handleStartPayment = async () => {
    if (isPaying) return;

    try {
      setIsPaying(true);

      const res = await startPayment(couponCode);

      if (!res.data.payment_url) {
        alert("این سفارش قبلاً پرداخت شده است.");
        navigate("/");
        return;
      }

      window.location.href = res.data.payment_url;
    } catch (error) {
      alert(
        "خطا در شروع پرداخت: " +
          (error.response?.data?.error?.message || "")
      );

      navigate("/checkout/checkout/");
    } finally {
      setIsPaying(false);
    }
  };

  if (!checkout) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-100 bg-white px-10 py-8 shadow-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#1E3A5F]" />
          <p className="text-sm font-medium text-slate-600">
            در حال دریافت اطلاعات سفارش...
          </p>
        </div>
      </div>
    );
  }

  const address = checkout.destination_address;

  return (
    <main className="min-h-screen bg-slate-50 py-6 sm:py-10">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header */}
        <section className="mb-6 rounded-2xl bg-gradient-to-l from-[#1E3A5F] to-[#294c78] px-5 py-6 text-white shadow-lg shadow-slate-200 sm:px-8 sm:py-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm text-blue-100">
                <FiCheckCircle className="h-4 w-4" />
                <span>مرحله نهایی ثبت سفارش</span>
              </div>

              <h1 className="text-2xl font-bold sm:text-3xl">
                بررسی و پرداخت سفارش
              </h1>

              <p className="mt-2 text-sm leading-6 text-blue-100">
                اطلاعات سفارش را بررسی کنید و سپس وارد درگاه پرداخت شوید.
              </p>
            </div>

            <div className="flex items-center gap-2 self-start rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm sm:self-auto">
              <FiShield className="h-5 w-5 text-[#FFC107]" />
              <span>پرداخت امن</span>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {/* Main content */}
          <div className="space-y-6 xl:col-span-8">
            {/* Address */}
            <section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
              <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[#1E3A5F]">
                  <FiMapPin className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="font-bold text-slate-800">آدرس تحویل سفارش</h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    سفارش به آدرس زیر ارسال خواهد شد.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 px-5 py-5 sm:grid-cols-2 sm:px-6">
                <div>
                  <p className="mb-1 text-xs font-medium text-slate-400">
                    عنوان آدرس
                  </p>
                  <p className="font-semibold text-slate-700">
                    {address?.title || "-"}
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium text-slate-400">
                    استان و شهر
                  </p>
                  <p className="font-semibold text-slate-700">
                    {address?.province_detail || "-"}{" "}
                    <span className="mx-1 text-slate-300">|</span>{" "}
                    {address?.city_detail || "-"}
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <p className="mb-1 text-xs font-medium text-slate-400">
                    نشانی کامل
                  </p>
                  <p className="leading-7 text-slate-700">
                    {address?.address_text || "-"}
                  </p>
                </div>
              </div>
            </section>

            {/* Shipments */}
            <section>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">
                    محموله‌های سفارش
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    اقلام سفارش بر اساس تأمین‌کننده و روش ارسال دسته‌بندی شده‌اند.
                  </p>
                </div>

                <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-600">
                  {checkout.shipments?.length || 0} محموله
                </span>
              </div>

              <div className="space-y-5">
                {checkout.shipments?.map((shipment) => (
                  <ReviewShipmentCard key={shipment.id} shipment={shipment} />
                ))}
              </div>
            </section>

            {/* Coupon */}
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <CouponForm
                applyCoupon={applyCoupon}
                coupon_amount={checkout.coupon_amount}
              />
            </section>

            {/* Note */}
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-4">
                <h2 className="font-bold text-slate-800">توضیحات سفارش</h2>
                <p className="mt-1 text-sm text-slate-500">
                  در صورت نیاز، توضیحات خود را برای سفارش وارد کنید.
                </p>
              </div>

              <textarea
                className="min-h-28 w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#1E3A5F] focus:bg-white focus:ring-4 focus:ring-blue-50"
                rows="4"
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                placeholder="برای مثال: هماهنگی قبل از ارسال، ساعت مناسب تحویل و..."
              />
            </section>

            {/* Payment method */}
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <PaymentMethodSelector
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-5 xl:col-span-4">
            <div className="xl:sticky xl:top-6">
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-md shadow-slate-200/60">
                <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
                  <h2 className="font-bold text-slate-800">خلاصه صورت‌حساب</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    مبالغ سفارش به تومان
                  </p>
                </div>

                <div className="p-5 sm:p-6">
                  <OrderSummary
                    subtotal_amount={checkout.subtotal_amount}
                    discount_amount={checkout.discount_amount}
                    coupon_amount={checkout.coupon_amount}
                    total_amount={checkout.total_amount}
                    shipping_amount={checkout.shipping_amount}
                    tax_amount={checkout.tax_amount}
                    payable_amount={checkout.payable_amount}
                  />

                  <button
                    type="button"
                    onClick={handleStartPayment}
                    disabled={isPaying}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1E3A5F] px-5 py-4 text-sm font-bold text-white transition hover:bg-[#17304f] focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isPaying ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        در حال انتقال...
                      </>
                    ) : (
                      <>
                        <span>پرداخت و ثبت نهایی سفارش</span>
                        <FiArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/checkout/checkout/")}
                    className="mt-3 w-full rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:border-[#1E3A5F] hover:bg-slate-50 hover:text-[#1E3A5F]"
                  >
                    بازگشت و ویرایش سفارش
                  </button>
                </div>
              </div>

              <p className="mt-4 px-2 text-center text-xs leading-6 text-slate-400">
                با انتخاب گزینه پرداخت، صحت اطلاعات سفارش را تأیید می‌کنید.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
